
// Comprehensive validation utilities

// Email validation with robust regex
export const validateEmail = (email, lang = 'en') => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!email || email.trim() === '') {
        return { valid: false, error: lang === 'en' ? 'Email is required' : 'البريد الإلكتروني مطلوب' };
    }

    if (!emailRegex.test(email)) {
        return {
            valid: false,
            error: lang === 'en' ? 'Oops! That email looks a bit off' : 'عذراً، البريد الإلكتروني غير صحيح'
        };
    }

    return { valid: true, error: null };
};

// Phone number validation with country code support and specific digit lengths
export const validatePhone = (phone, countryCodeObject = null, lang = 'en') => {
    const cleanPhone = phone.replace(/[^\d+]/g, '');

    if (!cleanPhone || cleanPhone.trim() === '') {
        return { valid: false, error: lang === 'en' ? 'Phone number is required' : 'رقم الهاتف مطلوب' };
    }

    if (countryCodeObject) {
        let numberPart = cleanPhone;
        if (cleanPhone.startsWith(countryCodeObject.code)) {
            numberPart = cleanPhone.slice(countryCodeObject.code.length);
        }

        if (countryCodeObject.digits) {
            const allowedDigits = Array.isArray(countryCodeObject.digits) ? countryCodeObject.digits : [countryCodeObject.digits];

            if (!allowedDigits.includes(numberPart.length)) {
                return {
                    valid: false,
                    error: lang === 'en' ? 'Invalid number' : 'رقم غير صحيح'
                };
            }
        } else {
            if (numberPart.length < 4 || numberPart.length > 15) {
                return {
                    valid: false,
                    error: lang === 'en' ? 'Invalid number' : 'رقم غير صحيح'
                };
            }
        }
        return { valid: true, error: null, formatted: `${countryCodeObject.code} ${numberPart}` };
    }

    if (cleanPhone.startsWith('+')) {
        const internationalRegex = /^\+\d{7,15}$/;
        if (!internationalRegex.test(cleanPhone)) {
            return {
                valid: false,
                error: lang === 'en' ? 'Invalid number' : 'رقم غير صحيح'
            };
        }
    } else {
        const localRegex = /^\d{7,15}$/;
        if (!localRegex.test(cleanPhone)) {
            return {
                valid: false,
                error: lang === 'en' ? 'Invalid number' : 'رقم غير صحيح'
            };
        }
    }

    return { valid: true, error: null, formatted: cleanPhone };
};

// Name validation
export const validateName = (name, lang = 'en') => {
    if (!name || name.trim() === '') {
        return { valid: false, error: lang === 'en' ? 'Tell us your name!' : 'أخبرنا باسمك!' };
    }

    if (name.trim().length < 3) {
        return {
            valid: false,
            error: lang === 'en' ? 'Almost there! 3 characters minimum' : 'قليلاً بعد! ٣ أحرف على الأقل'
        };
    }

    if (name.trim().length > 30) {
        return {
            valid: false,
            error: lang === 'en' ? 'A bit too long! Keep it under 30' : 'الاسم طويل جداً! اجعله أقل من ٣٠ حرف'
        };
    }

    const nameRegex = /^[a-zA-Z\u0600-\u06FF\s'-]+$/;
    if (!nameRegex.test(name)) {
        return {
            valid: false,
            error: lang === 'en' ? 'Letters and spaces only, please!' : 'الحروف والمسافات فقط، من فضلك!'
        };
    }

    return { valid: true, error: null };
};

// Message validation (20-1000 words logic)
export const validateMessage = (message, lang = 'en') => {
    if (!message || message.trim() === '') {
        return { valid: false, error: lang === 'en' ? 'How can we help you?' : 'كيف يمكننا مساعدتك؟' };
    }

    const words = message.trim().split(/\s+/).filter(word => word.length > 0);

    if (words.length < 20) {
        return {
            valid: false,
            error: lang === 'en' ? 'Minimum 20 words' : '٢٠ كلمة على الأقل'
        };
    }

    if (words.length > 1000) {
        return {
            valid: false,
            error: lang === 'en' ? 'Length exceeded' : 'تم تجاوز الحد المسموح'
        };
    }

    return { valid: true, error: null };
};

// Formatter
export const formatPhoneNumber = (phone, countryCode) => {
    const cleanPhone = phone.replace(/[^\d]/g, '');
    if (countryCode) {
        return `${countryCode} ${cleanPhone}`;
    }
    return cleanPhone;
};

export const sanitizeInput = (input) => {
    if (!input) return '';
    return input
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;')
        .replace(/\//g, '&#x2F;');
};

export const countryCodes = [
    { code: '+93', country: 'Afghanistan', flag: '🇦🇫', digits: 9 },
    { code: '+355', country: 'Albania', flag: '🇦🇱', digits: [3, 4, 5, 6, 7, 8, 9] },
    { code: '+213', country: 'Algeria', flag: '🇩🇿', digits: [8, 9] },
    { code: '+1684', country: 'American Samoa', flag: '🇦🇸', digits: 10 },
    { code: '+376', country: 'Andorra', flag: '🇦🇩', digits: [6, 8, 9] },
    { code: '+244', country: 'Angola', flag: '🇦🇴', digits: 9 },
    { code: '+1264', country: 'Anguilla', flag: '🇦🇮', digits: 10 },
    { code: '+672', country: 'Antarctica', flag: '🇦🇶' },
    { code: '+1268', country: 'Antigua & Barbuda', flag: '🇦🇬', digits: 10 },
    { code: '+54', country: 'Argentina', flag: '🇦🇷', digits: 10 },
    { code: '+374', country: 'Armenia', flag: '🇦🇲', digits: 8 },
    { code: '+297', country: 'Aruba', flag: '🇦🇼', digits: 7 },
    { code: '+61', country: 'Australia', flag: '🇦🇺', digits: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] },
    { code: '+43', country: 'Austria', flag: '🇦🇹', digits: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13] },
    { code: '+994', country: 'Azerbaijan', flag: '🇦🇿', digits: [8, 9] },
    { code: '+1242', country: 'Bahamas', flag: '🇧🇸', digits: 10 },
    { code: '+973', country: 'Bahrain', flag: '🇧🇭', digits: 8 },
    { code: '+880', country: 'Bangladesh', flag: '🇧🇩', digits: [6, 7, 8, 9, 10] },
    { code: '+1246', country: 'Barbados', flag: '🇧🇧', digits: 10 },
    { code: '+375', country: 'Belarus', flag: '🇧🇾', digits: [9, 10] },
    { code: '+32', country: 'Belgium', flag: '🇧🇪', digits: [8, 9] },
    { code: '+501', country: 'Belize', flag: '🇧🇿', digits: 7 },
    { code: '+229', country: 'Benin', flag: '🇧🇯', digits: 8 },
    { code: '+1441', country: 'Bermuda', flag: '🇧🇲', digits: 10 },
    { code: '+975', country: 'Bhutan', flag: '🇧🇹', digits: [7, 8] },
    { code: '+591', country: 'Bolivia', flag: '🇧🇴', digits: 8 },
    { code: '+387', country: 'Bosnia & Herzegovina', flag: '🇧🇦', digits: 8 },
    { code: '+267', country: 'Botswana', flag: '🇧🇼', digits: [7, 8] },
    { code: '+55', country: 'Brazil', flag: '🇧🇷', digits: 10 },
    { code: '+246', country: 'British Indian Ocean Territory', flag: '🇮🇴' },
    { code: '+1284', country: 'British Virgin Islands', flag: '🇻🇬', digits: 10 },
    { code: '+673', country: 'Brunei', flag: '🇧🇳', digits: 7 },
    { code: '+359', country: 'Bulgaria', flag: '🇧🇬', digits: [7, 8, 9] },
    { code: '+226', country: 'Burkina Faso', flag: '🇧🇫', digits: 8 },
    { code: '+257', country: 'Burundi', flag: '🇧🇮', digits: 8 },
    { code: '+855', country: 'Cambodia', flag: '🇰🇭', digits: 8 },
    { code: '+237', country: 'Cameroon', flag: '🇨🇲', digits: 8 },
    { code: '+1', country: 'Canada', flag: '🇨🇦', digits: 10 },
    { code: '+238', country: 'Cape Verde', flag: '🇨🇻', digits: 7 },
    { code: '+599', country: 'Caribbean Netherlands', flag: '🇧🇶' },
    { code: '+1345', country: 'Cayman Islands', flag: '🇰🇾', digits: 10 },
    { code: '+236', country: 'Central African Republic', flag: '🇨🇫', digits: 8 },
    { code: '+235', country: 'Chad', flag: '🇹🇩', digits: 8 },
    { code: '+56', country: 'Chile', flag: '🇨🇱', digits: [8, 9] },
    { code: '+86', country: 'China', flag: '🇨🇳', digits: [5, 6, 7, 8, 9, 10, 11, 12] },
    { code: '+61', country: 'Christmas Island', flag: '🇨🇽' },
    { code: '+61', country: 'Cocos (Keeling) Islands', flag: '🇨🇨' },
    { code: '+57', country: 'Colombia', flag: '🇨🇴', digits: [8, 10] },
    { code: '+269', country: 'Comoros', flag: '🇰🇲', digits: 7 },
    { code: '+242', country: 'Congo - Brazzaville', flag: '🇨🇬' },
    { code: '+243', country: 'Congo - Kinshasa', flag: '🇨🇩', digits: [5, 6, 7, 8, 9] },
    { code: '+682', country: 'Cook Islands', flag: '🇨🇰', digits: 5 },
    { code: '+506', country: 'Costa Rica', flag: '🇨🇷', digits: 8 },
    { code: '+225', country: 'Côte d’Ivoire', flag: '🇨🇮', digits: 8 },
    { code: '+385', country: 'Croatia', flag: '🇭🇷', digits: [8, 9, 10, 11, 12] },
    { code: '+53', country: 'Cuba', flag: '🇨🇺', digits: [6, 7, 8] },
    { code: '+599', country: 'Curaçao', flag: '🇨🇼' },
    { code: '+357', country: 'Cyprus', flag: '🇨🇾', digits: [8, 11] },
    { code: '+420', country: 'Czechia', flag: '🇨🇿', digits: [4, 5, 6, 7, 8, 9, 10, 11, 12] },
    { code: '+45', country: 'Denmark', flag: '🇩🇰', digits: 8 },
    { code: '+253', country: 'Djibouti', flag: '🇩🇯', digits: 6 },
    { code: '+1767', country: 'Dominica', flag: '🇩🇲', digits: 10 },
    { code: '+1', country: 'Dominican Republic', flag: '🇩🇴', digits: 10 },
    { code: '+593', country: 'Ecuador', flag: '🇪🇨', digits: 8 },
    { code: '+20', country: 'Egypt', flag: '🇪🇬', digits: [7, 8, 9] },
    { code: '+503', country: 'El Salvador', flag: '🇸🇻', digits: [7, 8, 11] },
    { code: '+240', country: 'Equatorial Guinea', flag: '🇬🇶', digits: 9 },
    { code: '+291', country: 'Eritrea', flag: '🇪🇷', digits: 7 },
    { code: '+372', country: 'Estonia', flag: '🇪🇪', digits: [7, 8, 9, 10] },
    { code: '+268', country: 'Eswatini', flag: '🇸🇿', digits: [7, 8] },
    { code: '+251', country: 'Ethiopia', flag: '🇪🇹', digits: 9 },
    { code: '+500', country: 'Falkland Islands', flag: '🇫🇰', digits: 5 },
    { code: '+298', country: 'Faroe Islands', flag: '🇫🇴', digits: 6 },
    { code: '+679', country: 'Fiji', flag: '🇫🇯', digits: 7 },
    { code: '+358', country: 'Finland', flag: '🇫🇮', digits: [5, 6, 7, 8, 9, 10, 11, 12] },
    { code: '+33', country: 'France', flag: '🇫🇷', digits: 9 },
    { code: '+594', country: 'French Guiana', flag: '🇬🇫', digits: 9 },
    { code: '+689', country: 'French Polynesia', flag: '🇵🇫', digits: 6 },
    { code: '+241', country: 'Gabon', flag: '🇬🇦', digits: [6, 7] },
    { code: '+220', country: 'Gambia', flag: '🇬🇲', digits: 7 },
    { code: '+995', country: 'Georgia', flag: '🇬🇪', digits: 9 },
    { code: '+49', country: 'Germany', flag: '🇩🇪', digits: [6, 7, 8, 9, 10, 11, 12, 13] },
    { code: '+233', country: 'Ghana', flag: '🇬🇭', digits: [5, 6, 7, 8, 9] },
    { code: '+350', country: 'Gibraltar', flag: '🇬🇮', digits: 8 },
    { code: '+30', country: 'Greece', flag: '🇬🇷', digits: 10 },
    { code: '+299', country: 'Greenland', flag: '🇬🇱', digits: 6 },
    { code: '+1473', country: 'Grenada', flag: '🇬🇩', digits: 10 },
    { code: '+590', country: 'Guadeloupe', flag: '🇬🇵', digits: 9 },
    { code: '+1671', country: 'Guam', flag: '🇬🇺', digits: 10 },
    { code: '+502', country: 'Guatemala', flag: '🇬🇹', digits: 8 },
    { code: '+44', country: 'Guernsey', flag: '🇬🇬' },
    { code: '+224', country: 'Guinea', flag: '🇬🇳', digits: 8 },
    { code: '+245', country: 'Guinea-Bissau', flag: '🇬🇼' },
    { code: '+592', country: 'Guyana', flag: '🇬🇾', digits: 7 },
    { code: '+509', country: 'Haiti', flag: '🇭🇹', digits: 8 },
    { code: '+504', country: 'Honduras', flag: '🇭🇳', digits: 8 },
    { code: '+852', country: 'Hong Kong SAR China', flag: '🇭🇰', digits: [4, 8, 9] },
    { code: '+36', country: 'Hungary', flag: '🇭🇺', digits: [8, 9] },
    { code: '+354', country: 'Iceland', flag: '🇮🇸', digits: [7, 9] },
    { code: '+91', country: 'India', flag: '🇮🇳', digits: [7, 8, 9, 10] },
    { code: '+62', country: 'Indonesia', flag: '🇮🇩', digits: [5, 6, 7, 8, 9, 10] },
    { code: '+98', country: 'Iran', flag: '🇮🇷', digits: [6, 7, 8, 9, 10] },
    { code: '+964', country: 'Iraq', flag: '🇮🇶', digits: [8, 9, 10] },
    { code: '+353', country: 'Ireland', flag: '🇮🇪', digits: [7, 8, 9, 10, 11] },
    { code: '+44', country: 'Isle of Man', flag: '🇮🇲' },
    { code: '+972', country: 'Israel', flag: '🇮🇱', digits: [8, 9] },
    { code: '+39', country: 'Italy', flag: '🇮🇹', digits: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] },
    { code: '+1876', country: 'Jamaica', flag: '🇯🇲', digits: 10 },
    { code: '+81', country: 'Japan', flag: '🇯🇵', digits: [5, 6, 7, 8, 9, 10, 11, 12, 13] },
    { code: '+44', country: 'Jersey', flag: '🇯🇪' },
    { code: '+962', country: 'Jordan', flag: '🇯🇴', digits: [5, 6, 7, 8, 9] },
    { code: '+7', country: 'Kazakhstan', flag: '🇰🇿', digits: 10 },
    { code: '+254', country: 'Kenya', flag: '🇰🇪', digits: [6, 7, 8, 9, 10] },
    { code: '+686', country: 'Kiribati', flag: '🇰🇮', digits: 5 },
    { code: '+383', country: 'Kosovo', flag: '🇽🇰' },
    { code: '+965', country: 'Kuwait', flag: '🇰🇼', digits: [7, 8] },
    { code: '+996', country: 'Kyrgyzstan', flag: '🇰🇬', digits: 9 },
    { code: '+856', country: 'Laos', flag: '🇱🇦', digits: [8, 9, 10] },
    { code: '+371', country: 'Latvia', flag: '🇱🇻', digits: [7, 8] },
    { code: '+961', country: 'Lebanon', flag: '🇱🇧', digits: [7, 8] },
    { code: '+266', country: 'Lesotho', flag: '🇱🇸', digits: 8 },
    { code: '+231', country: 'Liberia', flag: '🇱🇷', digits: [7, 8] },
    { code: '+218', country: 'Libya', flag: '🇱🇾', digits: [8, 9] },
    { code: '+423', country: 'Liechtenstein', flag: '🇱🇮', digits: [7, 8, 9] },
    { code: '+370', country: 'Lithuania', flag: '🇱🇹', digits: 8 },
    { code: '+352', country: 'Luxembourg', flag: '🇱🇺', digits: [4, 5, 6, 7, 8, 9, 10, 11] },
    { code: '+853', country: 'Macao SAR China', flag: '🇲🇴', digits: [7, 8] },
    { code: '+261', country: 'Madagascar', flag: '🇲🇬', digits: [9, 10] },
    { code: '+265', country: 'Malawi', flag: '🇲🇼', digits: [7, 8] },
    { code: '+60', country: 'Malaysia', flag: '🇲🇾', digits: [7, 8, 9] },
    { code: '+960', country: 'Maldives', flag: '🇲🇻', digits: 7 },
    { code: '+223', country: 'Mali', flag: '🇲🇱', digits: 8 },
    { code: '+356', country: 'Malta', flag: '🇲🇹', digits: 8 },
    { code: '+692', country: 'Marshall Islands', flag: '🇲🇭', digits: 7 },
    { code: '+596', country: 'Martinique', flag: '🇲🇶', digits: 9 },
    { code: '+222', country: 'Mauritania', flag: '🇲🇷', digits: 7 },
    { code: '+230', country: 'Mauritius', flag: '🇲🇺', digits: 7 },
    { code: '+262', country: 'Mayotte', flag: '🇾🇹' },
    { code: '+52', country: 'Mexico', flag: '🇲🇽', digits: 10 },
    { code: '+691', country: 'Micronesia', flag: '🇫🇲', digits: 7 },
    { code: '+373', country: 'Moldova', flag: '🇲🇩', digits: 8 },
    { code: '+377', country: 'Monaco', flag: '🇲🇨', digits: [5, 6, 7, 8, 9] },
    { code: '+976', country: 'Mongolia', flag: '🇲🇳', digits: [7, 8] },
    { code: '+382', country: 'Montenegro', flag: '🇲🇪', digits: [4, 5, 6, 7, 8, 9, 10, 11, 12] },
    { code: '+1664', country: 'Montserrat', flag: '🇲🇸', digits: 10 },
    { code: '+212', country: 'Morocco', flag: '🇲🇦', digits: 9 },
    { code: '+258', country: 'Mozambique', flag: '🇲🇿', digits: [8, 9] },
    { code: '+95', country: 'Myanmar (Burma)', flag: '🇲🇲', digits: [7, 8, 9] },
    { code: '+264', country: 'Namibia', flag: '🇳🇦', digits: [6, 7, 8, 9, 10] },
    { code: '+674', country: 'Nauru', flag: '🇳🇷', digits: [4, 7] },
    { code: '+977', country: 'Nepal', flag: '🇳🇵', digits: [8, 9] },
    { code: '+31', country: 'Netherlands', flag: '🇳🇱', digits: 9 },
    { code: '+687', country: 'New Caledonia', flag: '🇳🇨', digits: 6 },
    { code: '+64', country: 'New Zealand', flag: '🇳🇿', digits: [3, 4, 5, 6, 7, 8, 9, 10] },
    { code: '+505', country: 'Nicaragua', flag: '🇳🇮', digits: 8 },
    { code: '+227', country: 'Niger', flag: '🇳🇪', digits: 8 },
    { code: '+234', country: 'Nigeria', flag: '🇳🇬', digits: [7, 8, 9, 10] },
    { code: '+683', country: 'Niue', flag: '🇳🇺', digits: 4 },
    { code: '+672', country: 'Norfolk Island', flag: '🇳🇫' },
    { code: '+850', country: 'North Korea', flag: '🇰🇵', digits: [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17] },
    { code: '+389', country: 'North Macedonia', flag: '🇲🇰' },
    { code: '+1670', country: 'Northern Mariana Islands', flag: '🇲🇵' },
    { code: '+47', country: 'Norway', flag: '🇳🇴', digits: [5, 8] },
    { code: '+968', country: 'Oman', flag: '🇴🇲', digits: [7, 8] },
    { code: '+92', country: 'Pakistan', flag: '🇵🇰', digits: [8, 9, 10, 11] },
    { code: '+680', country: 'Palau', flag: '🇵🇼', digits: 7 },
    { code: '+970', country: 'Palestinian Territories', flag: '🇵🇸' },
    { code: '+507', country: 'Panama', flag: '🇵🇦', digits: [7, 8] },
    { code: '+675', country: 'Papua New Guinea', flag: '🇵🇬', digits: [4, 5, 6, 7, 8, 9, 10, 11] },
    { code: '+595', country: 'Paraguay', flag: '🇵🇾', digits: [5, 6, 7, 8, 9] },
    { code: '+51', country: 'Peru', flag: '🇵🇪', digits: [8, 9, 10, 11] },
    { code: '+63', country: 'Philippines', flag: '🇵🇭', digits: [8, 9, 10] },
    { code: '+48', country: 'Poland', flag: '🇵🇱', digits: [6, 7, 8, 9] },
    { code: '+351', country: 'Portugal', flag: '🇵🇹', digits: [9, 11] },
    { code: '+1', country: 'Puerto Rico', flag: '🇵🇷', digits: 10 },
    { code: '+974', country: 'Qatar', flag: '🇶🇦', digits: [3, 4, 5, 6, 7, 8] },
    { code: '+262', country: 'Réunion', flag: '🇷🇪' },
    { code: '+40', country: 'Romania', flag: '🇷🇴', digits: 9 },
    { code: '+7', country: 'Russia', flag: '🇷🇺', digits: 10 },
    { code: '+250', country: 'Rwanda', flag: '🇷🇼', digits: 9 },
    { code: '+590', country: 'St. Barthélemy', flag: '🇧🇱' },
    { code: '+290', country: 'St. Helena', flag: '🇸🇭' },
    { code: '+1869', country: 'St. Kitts & Nevis', flag: '🇰🇳' },
    { code: '+1758', country: 'St. Lucia', flag: '🇱🇨', digits: 10 },
    { code: '+590', country: 'St. Martin', flag: '🇲🇫' },
    { code: '+508', country: 'St. Pierre & Miquelon', flag: '🇵🇲' },
    { code: '+1784', country: 'St. Vincent & Grenadines', flag: '🇻🇨' },
    { code: '+685', country: 'Samoa', flag: '🇼🇸', digits: [3, 4, 5, 6, 7] },
    { code: '+378', country: 'San Marino', flag: '🇸🇲', digits: [6, 7, 8, 9, 10] },
    { code: '+239', country: 'São Tomé & Príncipe', flag: '🇸🇹' },
    { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦', digits: [8, 9] },
    { code: '+221', country: 'Senegal', flag: '🇸🇳', digits: 9 },
    { code: '+381', country: 'Serbia', flag: '🇷🇸', digits: [4, 5, 6, 7, 8, 9, 10, 11, 12] },
    { code: '+248', country: 'Seychelles', flag: '🇸🇨', digits: 7 },
    { code: '+232', country: 'Sierra Leone', flag: '🇸🇱', digits: 8 },
    { code: '+65', country: 'Singapore', flag: '🇸🇬', digits: [8, 9, 10, 11, 12] },
    { code: '+1721', country: 'Sint Maarten', flag: '🇸🇽', digits: 10 },
    { code: '+421', country: 'Slovakia', flag: '🇸🇰', digits: [4, 5, 6, 7, 8, 9] },
    { code: '+386', country: 'Slovenia', flag: '🇸🇮', digits: 8 },
    { code: '+677', country: 'Solomon Islands', flag: '🇸🇧', digits: 5 },
    { code: '+252', country: 'Somalia', flag: '🇸🇴', digits: [5, 6, 7, 8] },
    { code: '+27', country: 'South Africa', flag: '🇿🇦', digits: 9 },
    { code: '+500', country: 'South Georgia & South Sandwich Islands', flag: '🇬🇸' },
    { code: '+82', country: 'South Korea', flag: '🇰🇷', digits: [8, 9, 10, 11] },
    { code: '+211', country: 'South Sudan', flag: '🇸🇸' },
    { code: '+34', country: 'Spain', flag: '🇪🇸', digits: 9 },
    { code: '+94', country: 'Sri Lanka', flag: '🇱🇰', digits: 9 },
    { code: '+249', country: 'Sudan', flag: '🇸🇩', digits: 9 },
    { code: '+597', country: 'Suriname', flag: '🇸🇷', digits: [6, 7] },
    { code: '+47', country: 'Svalbard & Jan Mayen', flag: '🇸🇯' },
    { code: '+46', country: 'Sweden', flag: '🇸🇪', digits: [7, 8, 9, 10, 11, 12, 13] },
    { code: '+41', country: 'Switzerland', flag: '🇨🇭', digits: [4, 5, 6, 7, 8, 9, 10, 11, 12] },
    { code: '+963', country: 'Syria', flag: '🇸🇾', digits: [8, 9, 10] },
    { code: '+886', country: 'Taiwan', flag: '🇹🇼', digits: [8, 9] },
    { code: '+992', country: 'Tajikistan', flag: '🇹🇯', digits: 9 },
    { code: '+255', country: 'Tanzania', flag: '🇹🇿', digits: 9 },
    { code: '+66', country: 'Thailand', flag: '🇹🇭', digits: [8, 9] },
    { code: '+670', country: 'Timor-Leste', flag: '🇹🇱' },
    { code: '+228', country: 'Togo', flag: '🇹🇬', digits: 8 },
    { code: '+690', country: 'Tokelau', flag: '🇹🇰', digits: 4 },
    { code: '+676', country: 'Tonga', flag: '🇹🇴', digits: [5, 7] },
    { code: '+1868', country: 'Trinidad & Tobago', flag: '🇹🇹', digits: 10 },
    { code: '+216', country: 'Tunisia', flag: '🇹🇳', digits: 8 },
    { code: '+90', country: 'Turkey', flag: '🇹🇷', digits: 10 },
    { code: '+993', country: 'Turkmenistan', flag: '🇹🇲', digits: 8 },
    { code: '+1649', country: 'Turks & Caicos Islands', flag: '🇹🇨', digits: 10 },
    { code: '+688', country: 'Tuvalu', flag: '🇹🇻', digits: [5, 6] },
    { code: '+256', country: 'Uganda', flag: '🇺🇬', digits: 9 },
    { code: '+380', country: 'Ukraine', flag: '🇺🇦', digits: 9 },
    { code: '+971', country: 'United Arab Emirates', flag: '🇦🇪', digits: [8, 9] },
    { code: '+44', country: 'United Kingdom', flag: '🇬🇧', digits: [7, 8, 9, 10] },
    { code: '+1', country: 'United States', flag: '🇺🇸', digits: 10 },
    { code: '+598', country: 'Uruguay', flag: '🇺🇾', digits: [4, 5, 6, 7, 8, 9, 10, 11] },
    { code: '+998', country: 'Uzbekistan', flag: '🇺🇿', digits: 9 },
    { code: '+678', country: 'Vanuatu', flag: '🇻🇺', digits: [5, 7] },
    { code: '+39', country: 'Vatican City', flag: '🇻🇦', digits: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] },
    { code: '+58', country: 'Venezuela', flag: '🇻🇪', digits: 10 },
    { code: '+84', country: 'Vietnam', flag: '🇻🇳', digits: [7, 8, 9, 10] },
    { code: '+681', country: 'Wallis & Futuna', flag: '🇼🇫', digits: 6 },
    { code: '+212', country: 'Western Sahara', flag: '🇪🇭' },
    { code: '+967', country: 'Yemen', 'flag': '🇾🇪', digits: [6, 7, 8, 9] },
    { code: '+260', country: 'Zambia', flag: '🇿🇲', digits: 9 },
    { code: '+263', country: 'Zimbabwe', flag: '🇿🇼', digits: [5, 6, 7, 8, 9, 10] },
];
